import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMeFavoriteItemsV1 } from '@/services/platform/api/me-—-favorites/me-—-favorites';
import { useFavoriteLists } from './useFavoriteLists';

export interface UseShopFavoriteIndexResult {
  /** The user's 'Shop' favorite lists. */
  shopFavoriteLists: ReturnType<typeof useFavoriteLists>['data'];
  /** Product publicGuid -> the favorite list containing it, for every favorited Shop product. */
  favoriteListGuidByProductId: Map<string, string>;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Batch-resolves which Shop favorite list (if any) contains each product, for an entire
 * product grid in one shot. Fetches each Shop list's items once (there are typically only
 * a handful of lists), instead of one `/favorites/lookup` request per product on screen.
 */
export function useShopFavoriteIndex(): UseShopFavoriteIndexResult {
  const { data: favoriteLists, refetch: refetchLists } = useFavoriteLists();
  const shopFavoriteLists = useMemo(() => favoriteLists.filter(l => l.listType === 'Shop'), [favoriteLists]);
  const shopFavoriteListsWithGuid = useMemo(
    () => shopFavoriteLists.filter((l): l is typeof l & { publicGuid: string } => l.publicGuid != null),
    [shopFavoriteLists]
  );

  const [favoriteListGuidByProductId, setFavoriteListGuidByProductId] = useState<Map<string, string>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(false);

  const refetchItems = useCallback(async () => {
    if (shopFavoriteListsWithGuid.length === 0) {
      setFavoriteListGuidByProductId(new Map());
      return;
    }
    setIsLoading(true);
    try {
      const responses = await Promise.all(
        shopFavoriteListsWithGuid.map(list => getMeFavoriteItemsV1(list.publicGuid))
      );
      const map = new Map<string, string>();
      responses.forEach((response, index) => {
        if (response.status !== 200) return;
        const listGuid = shopFavoriteListsWithGuid[index].publicGuid;
        response.data.forEach(item => {
          if (item.itemType === 'Product' && item.itemExternalId) {
            map.set(item.itemExternalId, listGuid);
          }
        });
      });
      setFavoriteListGuidByProductId(map);
    } finally {
      setIsLoading(false);
    }
  }, [shopFavoriteListsWithGuid]);

  useEffect(() => {
    refetchItems();
  }, [refetchItems]);

  const refetch = useCallback(async () => {
    await refetchLists();
    await refetchItems();
  }, [refetchLists, refetchItems]);

  return { shopFavoriteLists, favoriteListGuidByProductId, isLoading, refetch };
}
